import { Dropdown, Form, Select } from 'antd';

import DeleteOutlined from '../../../../assets/common/DeleteOutlined';
import PlusRoundOutlined from '../../../../assets/common/PlusRoundOutlined';
import HelpCenterIcon from '../../../../assets/HelpCenterIcon';
import { AppForm } from '../../../../CommonComponents/AppForm/AppForm';
import AppInput from '../../../../CommonComponents/UI/AppInput/AppInput';
import AppTextArea from '../../../../CommonComponents/UI/AppTextArea/AppTextArea';
import styles from './PromptContainer.module.scss';

const OUTPUT_TYPE_OPTIONS = [
  { value: 'Text', label: 'Text' },
  { value: 'Date', label: 'Date' },
  { value: 'Number', label: 'Number' },
];

const popupRenderExtraction = () => {
  return (
    <section className={styles.prompt_help_dropdown_section}>
      <p className='mb-16'>
        Include both the field name and its scope in this instruction and select an output type. You
        may mention where this is commonly found in the document, what should be included and what
        should be excluded.
      </p>
      <p>
        Example: Termination for Convenience: Extract clauses that allow a party to terminate
        without cause by giving notice. This is usually found in the term and termination section of
        the document. Include express termination without cause rights and any related notice
        requirement. Exclude termination for breach, insolvency, or force majeure termination
        rights.
      </p>
    </section>
  );
};

const popupRenderOutputFields = () => {
  return (
    <section className={styles.prompt_help_dropdown_section}>
      <p className='mb-16'>
        Add one or more output sub-fields for the main extraction field above. These will define
        what values are returned for that extraction. For each sub-field, enter an output field name
        and select its type.
      </p>
      <p>
        Example: If the main extraction field is Termination for Convenience, the additional fields
        can be clause reference, clause text, who can terminate, notice period etc.
      </p>
    </section>
  );
};

const ExtractionFields = ({ field }) => {
  return (
    <div className='fs-12'>
      <Dropdown popupRender={popupRenderExtraction} destroyOnHidden>
        <span role='img' className={styles.extraction_instruction_eye}>
          <HelpCenterIcon size={16} fill='#738090' />
        </span>
      </Dropdown>
      {/* Extraction Field Name */}
      <AppForm.Item
        label='Extraction Field Name'
        name={[field.name, 'extractionFieldName']}
        rules={[{ required: true, message: 'This field name is required' }]}
        className={styles.input}
      >
        <AppInput placeholder='Enter field name' />
      </AppForm.Item>

      {/* Extraction Instruction */}
      <AppForm.Item
        label='Extraction Instruction'
        name={[field.name, 'extractionInstruction']}
        rules={[{ required: true, message: 'This field is required' }]}
        className={styles.input}
      >
        <AppTextArea
          id='extraction-instruction-text'
          placeholder='Define what should be extracted.'
          rows={3}
        />
      </AppForm.Item>
      {/* Output Type */}
      <div className='flex gap-8 align-center'>
        <p className='fs-13 fw-500'>
          Output Type<span className='color-danger'>*</span>
        </p>
        <AppForm.Item
          name={[field.name, 'outputType']}
          rules={[{ required: true, message: 'Output type is required' }]}
          noStyle
        >
          <Select
            options={OUTPUT_TYPE_OPTIONS}
            placeholder='Select'
            classNames={{
              popup: { root: styles.select_input_field_popup },
            }}
          />
        </AppForm.Item>
      </div>

      {/* Additional Output Fields (nested Form.List) */}
      <Form.List
        name={[field.name, 'additionalOutputFields']}
        initialValue={[{ id: crypto.randomUUID(), fieldName: '', outputType: 'Text' }]}
      >
        {(subFields, { add: addSub, remove: removeSub }) => (
          <div className={`${styles.output_fields} mt-16`}>
            <div className={styles.title}>
              <p className='fs-13 fw-500 mb-8'>
                Additional Output Fields <span className={styles.info_text}>(Optional)</span>
              </p>
              <Dropdown popupRender={popupRenderOutputFields} destroyOnHidden>
                <span role='img' className={styles.extraction_instruction_eye}>
                  <HelpCenterIcon size={16} fill='#738090' />
                </span>
              </Dropdown>
            </div>

            <ul>
              {subFields.map((subField) => (
                <li key={subField.key} className='mb-16'>
                  {subFields.length > 1 && (
                    <button
                      type='button'
                      title='Remove'
                      className={styles.delete_btn}
                      onClick={() => removeSub(subField.name)}
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                  <AppForm.Item
                    label='Output Field Name'
                    name={[subField.name, 'fieldName']}
                    // noStyle
                    className={styles.output_filed_name_label}
                  >
                    <AppInput placeholder='e.g. Clause Reference, Notice Period' />
                  </AppForm.Item>

                  <div className='mt-16 flex gap-8 align-center'>
                    <p className='fw-500'>Output Type</p>
                    <AppForm.Item name={[subField.name, 'outputType']} noStyle>
                      <Select
                        options={OUTPUT_TYPE_OPTIONS}
                        placeholder='Select'
                        classNames={{
                          popup: { root: styles.select_input_field_popup },
                        }}
                      />
                    </AppForm.Item>
                  </div>
                </li>
              ))}
            </ul>

            <button
              type='button'
              className='flex gap-8 color-primary fw-500 align-center'
              onClick={() =>
                addSub({
                  id: crypto.randomUUID(),
                  fieldName: '',
                  outputType: 'Text',
                })
              }
            >
              <PlusRoundOutlined />
              <span>Add Output Field</span>
            </button>
          </div>
        )}
      </Form.List>
    </div>
  );
};

export default ExtractionFields;
